import {
  errorForm,
  interaction,
  logger,
  navigation,
  alert,
} from '@origin-digital/reporting-client';
import { History } from 'history';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { VERIFY_ONE_TIME_CODE } from 'ts/constants/Auth0ErrorCodes';
import * as UrlParams from 'ts/constants/UrlParams';
import { setFlow } from 'ts/passwordless-login/services/FlowService';
import * as Auth0Service from '../../../../services/auth0/Auth0Service';
import PageConfig from '../../../../services/userflow/PageConfig';
import { FlowType } from '../../../../services/userflow/UserFlowService';
import { getDeviceId } from '../../../../url/UrlUtils';
import { OneTimeCodeView } from './OneTimeCodeView';

export interface OTCLocationState {
  hideTryAgainLink?: boolean;
}
interface OneTimeCodePageProps extends RouteComponentProps<{}, {}, OTCLocationState> {
  config: PageConfig;
  flow: FlowType;
  navigateToErrorPage: () => void;
}
type OtcAlertStateKeys =
  | 'resendOtcSuccess'
  | 'resendOtcBlocked'
  | 'resendOtcFailed'
  | 'bigPondEmail';
type OtcAlertState = {
  [key in OtcAlertStateKeys]: boolean;
};

const restartFlowIfArrivedViaBackOrForward = (history: History, config: PageConfig) => {
  if (history.action === 'POP') {
    // we must go to the correct path here as
    // on native, the url could be query params based or path based
    // depending on how the user arrived
    // querybased - user clicked on native link or from desktop
    // pathbased - user clicked on web link on native
    history.push(config.startRoute);
  }
};

const onTryAnotherClicked = (history: History, config: PageConfig) => (event) => {
  event.preventDefault();
  interaction('click', {
    componentType: 'link',
    id: 'try-another-linked-email-or-mobile',
    parentContainer: 'onetimecode-form',
    appName: config.analytics.taskName,
    label: 'try another linked email or mobile',
  });
  history.goBack();
};

const initialAlertState = {
  resendOtcSuccess: false,
  resendOtcBlocked: false,
  resendOtcFailed: false,
  bigPondEmail: false,
};

const OneTimeCodePage: React.FC<OneTimeCodePageProps> = ({
  history,
  location,
  config,
  flow,
  navigateToErrorPage,
}) => {
  const [oneTimeCode, setOneTimeCode] = useState('');
  const [submitSpinner, setSubmitSpinner] = useState(false);
  const [resendSpinner, setResendSpinner] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [countdown, setCountdown] = useState<number>(Date.now() + 600000);
  const urlSearchParams = new URLSearchParams(location.search);
  const otcUsername = urlSearchParams.get(UrlParams.EMAIL_OR_PHONE) || '';
  const userProvidedUsername = urlSearchParams.get(UrlParams.USER_PROVIDED_USERNAME) || '';
  const [alertState, setAlertState] = useState<OtcAlertState>({
    ...initialAlertState,
    bigPondEmail: otcUsername.indexOf('@bigpond.') !== -1,
  });
  const [showWrongCodeError, setShowWrongCodeError] = useState(false);

  useEffect(() => {
    restartFlowIfArrivedViaBackOrForward(history, config);
    navigation('screen', {
      currentUri: '',
      friendlyUri: `originenergy:my-account:auth:${config.analytics.pageName}`,
    });
    sendAnalyticsStartedWithMobileUsingEmail();
    // set flow here to avoid any timing issues
    setFlow(flow); // required as hosted password page loses the flow
  }, []);

  const isEmail = (username: string = ''): boolean => {
    return username.indexOf('@') !== -1;
  };

  const sendAnalyticsStartedWithMobileUsingEmail = () => {
    // if they entered their mobile but got to 3 point or 4 point check
    // and found their email and ended up here
    const startedWithMobileUsingEmail =
      isEmail(otcUsername) && userProvidedUsername && !isEmail(userProvidedUsername);
    if (startedWithMobileUsingEmail) {
      errorForm({
        formName: config.analytics.taskName,
        errorMessage: 'mobile-number-invalid-email-found',
        errorCode: 'mobile-number-invalid-email-found',
      });
    }
  };

  const showAlert = (key: OtcAlertStateKeys) => {
    setAlertState({
      ...initialAlertState,
      [key]: true,
    });
  };

  const hideAlert = (key: OtcAlertStateKeys) => {
    setAlertState({
      ...alertState,
      [key]: false,
    });
  };

  const afterSubmit = (error, response) => {
    const deviceId = getDeviceId();
    if (error) {
      const { code } = error;
      if (
        code === VERIFY_ONE_TIME_CODE.INVALID_USER_PASSWORD ||
        code === VERIFY_ONE_TIME_CODE.ACCESS_DENIED ||
        (code === VERIFY_ONE_TIME_CODE.INVALID_REQUEST && oneTimeCode === '')
      ) {
        errorForm({
          formName: config.analytics.taskName,
          errorMessage: `${config.analytics.taskName}:invalid-OTC`,
          errorCode: 'invalid-OTC',
        });
        setSubmitSpinner(false);
        setShowWrongCodeError(true);
        logger.error(`Wrong OTC submitted for userId: ${otcUsername}, uniqueId: ${deviceId}`);
      } else {
        navigateToErrorPage();
      }
    }
    logger.info(`OTC verified successfully for userId: ${otcUsername}, uniqueId: ${deviceId}`);
  };

  const onFormSubmit = (event) => {
    event.preventDefault();
    setSubmitSpinner(true);
    Auth0Service.verifyOneTimeCode(otcUsername, oneTimeCode, flow, afterSubmit);
  };

  const onResendClicked = async () => {
    if (showTimer) {
      alert.base('notification', {
        alertMessage: 'Hold tight! Codes can take a while',
        alertCode: 'otc-ongoing-countdown',
      });
      showAlert('resendOtcBlocked');
      return;
    }
    interaction('click', {
      componentType: 'link',
      id: 'resend-code',
      parentContainer: 'onetimecode-form',
      appName: config.analytics.taskName,
      label: 'resend code',
    });
    setResendSpinner(true);
    try {
      await Auth0Service.triggerPasswordlessStart(otcUsername);
    } catch (error) {
      errorForm({
        formName: config.analytics.taskName,
        errorMessage: `${config.analytics.taskName}:unable-to-resend-OTC`,
        errorCode: 'unable-to-resend-OTC',
      });
      setResendSpinner(false);
      showAlert('resendOtcFailed');
      return;
    }
    setResendSpinner(false);
    alert.base('notification', {
      alertMessage: "Great! We've just sent a new code",
      alertCode: 'otc-resend-success',
    });
    showAlert('resendOtcSuccess');
    setCountdown(Date.now() + 600000);
    setShowTimer(true);
  };

  const onTimerComplete = () => {
    setAlertState({ ...initialAlertState }); // hide all alerts
    setShowTimer(false);
  };

  return (
    <OneTimeCodeView
      emailOrPhone={otcUsername}
      onResendClicked={onResendClicked}
      onTryAnotherClicked={onTryAnotherClicked(history, config)}
      onFormSubmit={onFormSubmit}
      resendSpinner={resendSpinner}
      onOneTimeCodeChanged={(event) => setOneTimeCode(event.target.value)}
      onTimerComplete={onTimerComplete}
      hideResendOtcSuccessMessage={() => hideAlert('resendOtcSuccess')}
      hideBigPondAlert={() => hideAlert('bigPondEmail')}
      wrongCodeSubmitted={showWrongCodeError}
      submitSpinner={submitSpinner}
      showTimer={showTimer}
      countdown={countdown}
      analyticsTaskName={config.analytics.taskName}
      showResendOtcAlert={alertState.resendOtcBlocked}
      showResendOtcSuccess={alertState.resendOtcSuccess}
      showBigPondAlert={alertState.bigPondEmail}
      showResendFailedMessage={alertState.resendOtcFailed}
      hideTryAgainLink={location.state && location.state.hideTryAgainLink}
    />
  );
};

export { OneTimeCodePage };
